import { inject, Injectable } from '@angular/core';
import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { Store } from '@ngxs/store';
import { Observable, combineLatest, map } from 'rxjs';
import {
  ToggleDisposition,
  ToggleItemSelection,
  ToggleItemsSelection,
  ApplyFilters,
  ResetFilters,
  SetPagination,
  GetData,
  ResetState,
  PatchItem,
  RemoveItem,
  LoadMore,
  NavigateCursor
} from '../actions/resource.actions';
import { ResourceState } from '../states/resource.state';
import { Resource } from '~core/models/shared.models';

@Injectable()
export class ResourceFacade {
  protected _store = inject(Store);

  disposition$ = this._store.select(ResourceState.disposition);

  resources$ = this._store.select(ResourceState.resources);

  currentError$ = this._store.select(ResourceState.currentError);

  selectedResources$ = this._store.select(ResourceState.selectedResources);

  selectedResource$ = this._store.select(ResourceState.selectedResource);

  paginationData$ = this._store.select(ResourceState.paginationData);

  paginationMode$ = this._store.select(ResourceState.paginationMode);

  hasPrevPage$ = this._store.select(ResourceState.hasPrevPage);

  cursorHistory$ = this._store.select(ResourceState.cursorHistory);

  filters$ = this._store.select(ResourceState.filters);

  areAllResourcesSelected$ = this._store.select(
    ResourceState.areAllResourcesSelected
  );

  haveSomeResourcesSelected$ = this._store.select(
    ResourceState.haveSomeResourcesSelected
  );

  isSelected$ = (item: Resource | null | undefined): Observable<boolean> =>
    this._store
      .select(ResourceState.isSelected)
      .pipe(map(filterFn => filterFn(item)));

  readonly isLoading$ = this._store.select(hasActionsExecuting([GetData]));

  readonly isLoadingInitialData$ = combineLatest([
    this.isLoading$,
    this.resources$
  ]).pipe(map(([isLoading, resources]) => !!isLoading && !resources.length));

  readonly isLoadingAdditionalData$ = combineLatest([
    this.isLoading$,
    this.resources$
  ]).pipe(map(([isLoading, resources]) => !!isLoading && !!resources.length));

  readonly resource = this._store.selectSignal(ResourceState.resources);
  readonly paginationData = this._store.selectSignal(
    ResourceState.paginationData
  );

  // Action Dispatchers
  resetState(
    ...params: ConstructorParameters<typeof ResetState>
  ): Observable<unknown> {
    return this._store.dispatch(new ResetState(...params));
  }

  toggleDisposition(
    ...params: ConstructorParameters<typeof ToggleDisposition>
  ): Observable<unknown> {
    return this._store.dispatch(new ToggleDisposition(...params));
  }

  patchItem(
    ...params: ConstructorParameters<typeof PatchItem>
  ): Observable<unknown> {
    return this._store.dispatch(new PatchItem(...params));
  }

  toggleItemSelection(
    ...params: ConstructorParameters<typeof ToggleItemSelection>
  ): Observable<unknown> {
    return this._store.dispatch(new ToggleItemSelection(...params));
  }

  toggleItemsSelection(
    ...params: ConstructorParameters<typeof ToggleItemsSelection>
  ): Observable<unknown> {
    return this._store.dispatch(new ToggleItemsSelection(...params));
  }

  applyFilters(
    ...params: ConstructorParameters<typeof ApplyFilters>
  ): Observable<unknown> {
    return this._store.dispatch(new ApplyFilters(...params));
  }

  resetFilters(
    ...params: ConstructorParameters<typeof ResetFilters>
  ): Observable<unknown> {
    return this._store.dispatch(new ResetFilters(...params));
  }

  setPagination(
    ...params: ConstructorParameters<typeof SetPagination>
  ): Observable<unknown> {
    return this._store.dispatch(new SetPagination(...params));
  }

  getData(
    ...params: ConstructorParameters<typeof GetData>
  ): Observable<unknown> {
    return this._store.dispatch(new GetData(...params));
  }

  /**
   * Remove an item from the resources list optimistically
   */
  removeItem(
    ...params: ConstructorParameters<typeof RemoveItem>
  ): Observable<unknown> {
    return this._store.dispatch(new RemoveItem(...params));
  }

  loadMore(): Observable<unknown> {
    return this._store.dispatch(new LoadMore());
  }

  navigateNext(): Observable<unknown> {
    return this._store.dispatch(new NavigateCursor('next'));
  }

  navigatePrevious(): Observable<unknown> {
    return this._store.dispatch(new NavigateCursor('previous'));
  }
}
